import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import FormField from '../../components/ui/FormField.jsx';
import Modal from '../../components/ui/Modal.jsx';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import { confirmDelete, toastError, toastSuccess } from '../../hooks/useToast.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useUpload } from '../../hooks/useUpload.js';
import {
  ADMIN_DELETE_BTN_CLASS,
  ADMIN_ICON_BTN_CLASS,
  adminPublishStatusClasses,
} from '../../constants/adminUi.js';

const empty = {
  titulo: '',
  subtitulo: '',
  descripcion: '',
  imagen: '',
  boton_texto: '',
  boton_url: '',
  estado: true,
  orden: '',
};

export default function CarruselAdminPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);

  const { uploadFile, uploading } = useUpload();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: empty });

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/carrusel', {
        params: { page, per_page: perPage, search: debouncedSearch },
      });
      if (data.success) {
        setItems(data.data.items);
        setTotal(data.data.total);
      }
    } catch (e) {
      toastError('Error', e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch]);

  function openCreate() {
    setEditingId(null);
    reset(empty);
    setModalOpen(true);
  }

  async function openEdit(row) {
    setEditingId(row.id);
    reset({
      titulo: row.titulo ?? '',
      subtitulo: row.subtitulo ?? '',
      descripcion: row.descripcion ?? '',
      imagen: row.imagen ?? '',
      boton_texto: row.boton_texto ?? '',
      boton_url: row.boton_url ?? '',
      estado: Number(row.estado) === 1,
      orden: row.orden != null && row.orden !== '' ? String(row.orden) : '',
    });
    setModalOpen(true);
  }

  async function onSubmit(values) {
    const payload = {
      ...values,
      estado: values.estado ? 1 : 0,
    };
    if (values.orden === '' || values.orden == null) {
      delete payload.orden;
    } else {
      const n = Number(values.orden);
      if (!Number.isFinite(n)) delete payload.orden;
      else payload.orden = n;
    }
    try {
      if (editingId) {
        const { data } = await api.put(`/v1/carrusel/${editingId}`, payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Actualizado', data.message);
      } else {
        const { data } = await api.post('/v1/carrusel', payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Creado', data.message);
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toastError('No se pudo guardar', e.message);
    }
  }

  async function onDelete(row) {
    const ok = await confirmDelete('¿Eliminar slide?', row.titulo);
    if (!ok) return;
    try {
      const { data } = await api.delete(`/v1/carrusel/${row.id}`);
      if (!data.success) throw new Error(data.message);
      toastSuccess('Eliminado', data.message);
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function toggleEstado(row) {
    try {
      const current = await api.get(`/v1/carrusel/${row.id}`);
      const r = current.data?.data;
      if (!r) throw new Error('No se pudo leer el registro');
      const { data } = await api.put(`/v1/carrusel/${row.id}`, {
        ...r,
        estado: Number(r.estado) === 1 ? 0 : 1,
      });
      if (!data.success) throw new Error(data.message);
      toastSuccess('Estado actualizado', '');
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function onPickImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = await uploadFile(file);
      setValue('imagen', path, { shouldValidate: true });
      toastSuccess('Imagen lista', 'URL guardada en el formulario.');
    } catch (err) {
      toastError('Subida fallida', err.message);
    } finally {
      e.target.value = '';
    }
  }

  const columns = useMemo(
    () => [
      {
        key: 'orden',
        label: 'Orden',
        render: (row) => (
          <span className="font-mono text-sm tabular-nums text-slate-600">{row.orden ?? '—'}</span>
        ),
      },
      { key: 'titulo', label: 'Título' },
      {
        key: 'estado',
        label: 'Estado',
        render: (row) => (
          <button
            type="button"
            onClick={() => toggleEstado(row)}
            className={adminPublishStatusClasses(Number(row.estado) === 1)}
          >
            {Number(row.estado) === 1 ? 'Activo' : 'Inactivo'}
          </button>
        ),
      },
      {
        key: 'fecha_creacion',
        label: 'Creación',
        render: (row) => new Date(row.fecha_creacion).toLocaleString('es-CO'),
      },
      {
        key: 'actions',
        label: '',
        render: (row) => (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Vista previa"
              onClick={() => {
                setPreviewRow(row);
                setPreviewOpen(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Editar"
              onClick={() => openEdit(row)}
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={ADMIN_DELETE_BTN_CLASS}
              title="Eliminar"
              onClick={() => onDelete(row)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-accent-blue md:text-4xl">Carrusel principal</h1>
          <p className="mt-2 max-w-2xl text-slate-600">
            Gestiona los slides del hero público. El <strong className="text-slate-200">orden</strong> es la posición
            en el carrusel: <strong className="text-slate-200">1 sale de primeras</strong>, luego 2, 3… Si dejas orden
            vacío al crear, se asigna el siguiente al final.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Nuevo slide
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
      />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Editar slide' : 'Nuevo slide'}
        wide
      >
        <form className="grid gap-5 md:grid-cols-2" onSubmit={handleSubmit(onSubmit)}>
          <FormField label="Título" error={errors.titulo?.message}>
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('titulo', { required: 'Requerido' })}
            />
          </FormField>
          <FormField
            label="Orden en carrusel"
            hint="1 = primero en la web, 2 = segundo… Vacío al crear = al final (automático)."
            error={errors.orden?.message}
          >
            <input
              type="number"
              min={1}
              step={1}
              placeholder="1"
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('orden', {
                validate: (v) =>
                  v === '' || v == null || (Number.isFinite(Number(v)) && Number(v) >= 1) || 'Usa un entero ≥ 1',
              })}
            />
          </FormField>
          <FormField label="Subtítulo">
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('subtitulo')}
            />
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Descripción">
              <textarea
                rows={3}
                className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                {...register('descripcion')}
              />
            </FormField>
          </div>
          <FormField label="Texto botón">
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('boton_texto')}
            />
          </FormField>
          <FormField label="URL botón">
            <input
              className="w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
              {...register('boton_url')}
            />
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Imagen (subida)" hint={uploading ? 'Subiendo…' : 'PNG, JPG, WebP · máximo según servidor'}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input type="file" accept="image/*" onChange={onPickImage} disabled={uploading} />
                <input
                  type="text"
                  className="w-full flex-1 rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-xs text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40"
                  placeholder="uploads/…"
                  {...register('imagen')}
                />
              </div>
            </FormField>
          </div>
          <label className="flex items-center gap-3 text-sm text-slate-300 md:col-span-2">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-accent-lime-dark focus:ring-brand-green" {...register('estado')} />
            Activo en web pública
          </label>
          <div className="flex flex-wrap gap-3 md:col-span-2">
            <Button type="submit">{editingId ? 'Guardar cambios' : 'Crear slide'}</Button>
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Vista previa" wide>
        {previewRow && (
          <div className="grid gap-6 md:grid-cols-2">
            <div className="aspect-[16/10] overflow-hidden rounded-xl bg-slate-900 ring-1 ring-white/10">
              {previewRow.imagen ? (
                <img src={assetUrl(previewRow.imagen)} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-slate-500">Sin imagen</div>
              )}
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent-lime">{previewRow.subtitulo}</p>
              <h3 className="font-display text-3xl font-bold text-white">{previewRow.titulo}</h3>
              <p className="text-sm leading-relaxed text-slate-400">{previewRow.descripcion}</p>
              <div className="pt-4 text-sm text-slate-500">
                Botón:{' '}
                <span className="text-slate-300">
                  {previewRow.boton_texto || '—'} → {previewRow.boton_url || '—'}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
